import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { appToast } from './AppToast'
import { getJobStatus } from '../api/jobsApi'

export default function SharedBulkUploadScreen({
  eyebrow = 'Enterprise Management',
  title = 'Bulk Upload',
  subtitle = 'Import multiple records at once using a standardized Excel template.',
  returnUrl = '/',
  templates = [],
  uploadFn,
  entityLabelSingular = 'record',
  entityLabelPlural = 'records',
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const resolvedReturnUrl = location.state?.returnTo || returnUrl

  const [currentStep, setCurrentStep] = useState(1)
  const [selectedTemplate, setSelectedTemplate] = useState(templates[0] || null)
  const [downloadingKey, setDownloadingKey] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [file, setFile] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const [uploadResult, setUploadResult] = useState(null)
  const [importProgress, setImportProgress] = useState(0)
  const [activeJobId, setActiveJobId] = useState(null)
  const fileInputRef = useRef(null)
  const pollTimerRef = useRef(null)

  useEffect(() => {
    if (templates.length > 0 && !selectedTemplate) {
      setSelectedTemplate(templates[0])
    }
  }, [templates, selectedTemplate])

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
    }
  }, [])

  // -- Download Template Handler -----------------------------------------------
  const handleDownloadTemplate = async (tmpl) => {
    if (!tmpl?.downloadFn) return
    setDownloadingKey(tmpl.key)
    try {
      await tmpl.downloadFn()
      appToast.success(`Downloaded ${tmpl.title} template`)
    } catch (err) {
      console.error('[SharedBulkUploadScreen] download template error:', err)
      appToast.error('Failed to download template. Please try again.')
    } finally {
      setDownloadingKey(null)
    }
  }

  // -- Drag & Drop ------------------------------------------------------------
  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true)
    else if (e.type === 'dragleave') setDragActive(false)
  }

  const validateAndSetFile = (f) => {
    if (
      f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      f.name.endsWith('.xlsx')
    ) {
      setFile(f)
      setUploadResult(null)
      appToast.success(`File selected: ${f.name}`)
    } else {
      appToast.error('Please upload only Excel (.xlsx) files.')
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files?.[0]) validateAndSetFile(e.dataTransfer.files[0])
  }

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) validateAndSetFile(e.target.files[0])
  }

  // -- Polling Background Job --------------------------------------------------
  const _pollImportJob = (jobId, attempt = 0) => {
    if (attempt > 150) {
      setIsUploading(false)
      setImportProgress(0)
      setActiveJobId(null)
      appToast.error('Import timed out. Please check Job History or try again.')
      return
    }
    pollTimerRef.current = setTimeout(async () => {
      try {
        const job = await getJobStatus(jobId)
        setImportProgress(job.progressPct || 0)

        if (job.status === 'COMPLETED') {
          setIsUploading(false)
          setImportProgress(100)
          setActiveJobId(null)
          const report = job.resultJson || {}
          setUploadResult(report)
          if (report.failure_count === 0) {
            appToast.success(`All ${report.success_count} ${entityLabelPlural} imported successfully!`)
          } else if (report.success_count === 0) {
            appToast.error(`Import failed — ${report.failure_count} rows had errors.`)
          } else {
            appToast.warning(
              `Import completed with issues: ${report.success_count} created, ${report.failure_count} failed.`,
            )
          }
        } else if (job.status === 'FAILED') {
          setIsUploading(false)
          setImportProgress(0)
          setActiveJobId(null)
          appToast.error(`Import failed: ${job.errorMessage || 'Unknown error. Please try again.'}`)
        } else {
          _pollImportJob(jobId, attempt + 1)
        }
      } catch (err) {
        console.error('[SharedBulkUploadScreen] polling error:', err)
        _pollImportJob(jobId, attempt + 1)
      }
    }, 2000)
  }

  // -- Upload Trigger ---------------------------------------------------------
  const handleUploadFile = async () => {
    if (!file) {
      appToast.error('Please select an Excel file first.')
      return
    }
    if (!uploadFn) {
      appToast.error('Upload handler is not configured.')
      return
    }
    setIsUploading(true)
    setImportProgress(0)
    try {
      const response = await uploadFn(file)
      const jobId = response?.jobId || response?.id
      if (jobId) {
        setActiveJobId(jobId)
        appToast.info(`Import job started (Job #${jobId}). Processing in background without freezing!`)
        _pollImportJob(jobId)
      } else if (response && typeof response === 'object' && ('success_count' in response || 'total' in response)) {
        // Direct synchronous response fallback
        setIsUploading(false)
        setImportProgress(100)
        setUploadResult(response)
      }
    } catch (err) {
      console.error(err)
      setIsUploading(false)
      setImportProgress(0)
      appToast.error(
        err.response?.data?.detail || err.message || 'Failed to upload file. Verify the file format and try again.',
      )
    }
  }

  const handleReset = () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
    setFile(null)
    setUploadResult(null)
    setCurrentStep(1)
    setImportProgress(0)
    setActiveJobId(null)
    setIsUploading(false)
  }

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="mx-auto w-full max-w-[1320px] pb-8">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-primary">
            {eyebrow}
          </p>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">
            {title}
          </h1>
          <p className="mt-2 text-sm text-on-surface-variant">
            {subtitle}
          </p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-xl border border-outline px-4 py-2.5 text-sm font-semibold text-on-surface transition-all hover:bg-surface-container-high cursor-pointer select-none"
          onClick={() => navigate(resolvedReturnUrl)}
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back to List
        </button>
      </header>

      {/* ── Step Progress Indicator ─────────────────────────────────────── */}
      <div className="mb-8 rounded-2xl border border-outline-variant/30 bg-surface-container-low p-4 shadow-xs">
        <div className="flex items-center justify-between">
          {[
            { step: 1, label: 'Select & Download Template', icon: 'download' },
            { step: 2, label: 'Upload & Process', icon: 'upload_file' },
            { step: 3, label: 'Review Results', icon: 'check_circle' },
          ].map((item, idx) => {
            const isCompleted = uploadResult ? item.step <= 3 : currentStep > item.step
            const isCurrent = uploadResult ? item.step === 3 : currentStep === item.step

            return (
              <div key={item.step} className="flex flex-1 items-center">
                <button
                  type="button"
                  onClick={() => !isUploading && setCurrentStep(item.step)}
                  disabled={isUploading || (!uploadResult && item.step === 3)}
                  className={`flex items-center gap-3 text-left transition-all ${
                    isCurrent
                      ? 'text-primary font-bold'
                      : isCompleted
                      ? 'text-on-surface font-semibold'
                      : 'text-on-surface-variant/50 font-normal cursor-not-allowed'
                  }`}
                >
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold transition-all ${
                      isCurrent
                        ? 'bg-primary text-on-primary shadow-sm shadow-primary/30'
                        : isCompleted
                        ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/30'
                        : 'bg-surface-container-highest text-on-surface-variant/60'
                    }`}
                  >
                    {isCompleted && !isCurrent ? (
                      <span className="material-symbols-outlined text-base">check</span>
                    ) : (
                      item.step
                    )}
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-xs uppercase tracking-wider opacity-60">Step {item.step}</p>
                    <p className="text-sm leading-tight">{item.label}</p>
                  </div>
                </button>
                {idx < 2 && (
                  <div
                    className={`mx-4 h-[2px] flex-1 transition-all ${
                      isCompleted ? 'bg-primary/40' : 'bg-outline-variant/20'
                    }`}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* ── STEP 1: Download Templates ─────────────────────────────────── */}
      {currentStep === 1 && !uploadResult && (
        <section className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-on-surface">Step 1: Download Excel Template</h2>
            <p className="mt-1 text-sm text-on-surface-variant">
              Download the template pre-formatted with valid columns and example data. Fill in your {entityLabelPlural} and upload in Step 2.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {templates.map((tmpl) => {
              const isSelected = selectedTemplate?.key === tmpl.key
              const isDownloading = downloadingKey === tmpl.key

              return (
                <div
                  key={tmpl.key}
                  onClick={() => setSelectedTemplate(tmpl)}
                  className={`group relative flex flex-col justify-between rounded-2xl border p-6 transition-all cursor-pointer ${
                    isSelected
                      ? 'border-primary bg-primary/5 shadow-md shadow-primary/5'
                      : 'border-outline-variant/30 bg-surface-container-low hover:border-primary/40 hover:bg-surface-container'
                  }`}
                >
                  <div>
                    {/* Badge & Icon */}
                    <div className="flex items-center justify-between">
                      <div
                        className="flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-sm"
                        style={{ backgroundColor: tmpl.color || '#1E3A8A' }}
                      >
                        <span className="material-symbols-outlined text-2xl">{tmpl.icon || 'table_chart'}</span>
                      </div>
                      <span className="rounded-full bg-surface-container-highest px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                        {tmpl.badge || 'Template'}
                      </span>
                    </div>

                    {/* Title & Subtitle */}
                    <h3 className="mt-4 text-lg font-bold text-on-surface group-hover:text-primary transition-colors">
                      {tmpl.title}
                    </h3>
                    <p className="text-xs font-medium text-primary mt-0.5">{tmpl.subtitle}</p>
                    <p className="mt-2 text-xs leading-relaxed text-on-surface-variant">
                      {tmpl.description}
                    </p>

                    {/* Columns Preview */}
                    <div className="mt-4">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant/70 mb-1.5">
                        Columns ({tmpl.columns?.length || 0})
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {(tmpl.columns || []).slice(0, 6).map((col, cIdx) => (
                          <span
                            key={cIdx}
                            className="rounded-md bg-surface-container-highest/80 px-2 py-0.5 text-[10px] font-medium text-on-surface-variant"
                          >
                            {col}
                          </span>
                        ))}
                        {(tmpl.columns?.length || 0) > 6 && (
                          <span className="rounded-md bg-surface-container-highest/50 px-2 py-0.5 text-[10px] font-medium text-on-surface-variant/70">
                            +{tmpl.columns.length - 6} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-6 flex items-center gap-2 pt-4 border-t border-outline-variant/20">
                    <button
                      type="button"
                      disabled={isDownloading}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDownloadTemplate(tmpl)
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-3 py-2 text-xs font-bold text-on-primary shadow-sm hover:opacity-90 transition-opacity cursor-pointer"
                    >
                      <span className="material-symbols-outlined text-base">
                        {isDownloading ? 'sync' : 'download'}
                      </span>
                      {isDownloading ? 'Downloading...' : 'Download Template'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-on-primary shadow-md shadow-primary/20 hover:opacity-90 transition cursor-pointer"
            >
              Continue to Upload
              <span className="material-symbols-outlined text-base">arrow_forward</span>
            </button>
          </div>
        </section>
      )}

      {/* ── STEP 2: Upload & Processing ─────────────────────────────────── */}
      {currentStep === 2 && !uploadResult && (
        <section className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-on-surface">Step 2: Upload Completed Excel File</h2>
            <p className="mt-1 text-sm text-on-surface-variant">
              Upload your completed spreadsheet. The server will validate each row and process in the background.
            </p>
          </div>

          {/* Drag & Drop Zone */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => !isUploading && fileInputRef.current?.click()}
            className={`flex flex-col items-center justify-center rounded-3xl border-2 border-dashed p-10 text-center transition-all cursor-pointer ${
              dragActive
                ? 'border-primary bg-primary/10 scale-[0.99]'
                : file
                ? 'border-emerald-500/50 bg-emerald-500/5'
                : 'border-outline-variant/40 bg-surface-container-low hover:border-primary/50 hover:bg-surface-container'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
              className="hidden"
              disabled={isUploading}
            />

            <div
              className={`mb-4 flex h-16 w-16 items-center justify-center rounded-2xl ${
                file
                  ? 'bg-emerald-500/10 text-emerald-600'
                  : 'bg-primary/10 text-primary'
              }`}
            >
              <span className="material-symbols-outlined text-4xl">
                {file ? 'description' : 'upload_file'}
              </span>
            </div>

            {file ? (
              <div className="space-y-1">
                <p className="text-base font-bold text-on-surface">{file.name}</p>
                <p className="text-xs text-on-surface-variant font-medium">
                  {formatFileSize(file.size)} • Ready to upload
                </p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setFile(null)
                  }}
                  className="mt-2 text-xs font-bold text-error hover:underline"
                >
                  Remove & Choose Another
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-base font-bold text-on-surface">
                  Drag and drop your Excel file here
                </p>
                <p className="text-xs text-on-surface-variant">
                  or <span className="text-primary font-bold underline">browse from your computer</span> (.xlsx files only)
                </p>
              </div>
            )}
          </div>

          {/* Progress Bar while Uploading / Background Processing */}
          {isUploading && (
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 shadow-xs space-y-3">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined animate-spin text-primary text-xl">
                    progress_activity
                  </span>
                  <span className="font-bold text-on-surface">
                    {activeJobId ? `Processing in Background (Job #${activeJobId})...` : 'Uploading file...'}
                  </span>
                </div>
                <span className="font-extrabold text-primary">{importProgress}%</span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-container-highest">
                <div
                  className="h-full bg-primary transition-all duration-500 ease-out rounded-full"
                  style={{ width: `${Math.max(importProgress, 5)}%` }}
                />
              </div>
              <p className="text-xs text-on-surface-variant">
                Your file is being parsed and validated safely in an isolated background thread. You can keep this tab open or navigate freely.
              </p>
            </div>
          )}

          {/* Step Actions */}
          <div className="flex items-center justify-between pt-4">
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              disabled={isUploading}
              className="inline-flex items-center gap-2 rounded-xl border border-outline px-4 py-2.5 text-sm font-semibold text-on-surface hover:bg-surface-container-high transition cursor-pointer"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              Back to Templates
            </button>

            <button
              type="button"
              onClick={handleUploadFile}
              disabled={!file || isUploading}
              className={`inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-bold text-on-primary shadow-md transition cursor-pointer ${
                file && !isUploading
                  ? 'bg-primary shadow-primary/25 hover:opacity-90'
                  : 'bg-primary/40 cursor-not-allowed'
              }`}
            >
              <span className="material-symbols-outlined text-base">cloud_upload</span>
              {isUploading ? 'Importing...' : 'Start Import'}
            </button>
          </div>
        </section>
      )}

      {/* ── STEP 3: Review Results Report ───────────────────────────────── */}
      {uploadResult && (
        <section className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-on-surface">Step 3: Import Summary & Results</h2>
            <p className="mt-1 text-sm text-on-surface-variant">
              Here is the row-by-row breakdown of the processed file.
            </p>
          </div>

          {/* Metric Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low p-5 shadow-xs">
              <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Total Rows</p>
              <p className="mt-2 text-3xl font-black text-on-surface">{uploadResult.total || 0}</p>
            </div>

            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 shadow-xs">
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Created Successfully</p>
              <p className="mt-2 text-3xl font-black text-emerald-600">{uploadResult.success_count || 0}</p>
            </div>

            <div className="rounded-2xl border border-error/30 bg-error/5 p-5 shadow-xs">
              <p className="text-xs font-bold uppercase tracking-wider text-error">Failed / Skipped</p>
              <p className="mt-2 text-3xl font-black text-error">{uploadResult.failure_count || 0}</p>
            </div>
          </div>

          {/* Detailed Row-by-Row Table */}
          {Array.isArray(uploadResult.results) && uploadResult.results.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-low shadow-xs">
              <div className="px-6 py-4 border-b border-outline-variant/20 flex items-center justify-between">
                <h3 className="text-sm font-bold text-on-surface uppercase tracking-wider">
                  Row Processing Details
                </h3>
                <span className="text-xs text-on-surface-variant">
                  {uploadResult.results.length} rows processed
                </span>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead className="sticky top-0 bg-surface-container-highest text-on-surface-variant uppercase text-[10px] font-bold">
                    <tr>
                      <th className="px-4 py-3">Row</th>
                      <th className="px-4 py-3">Name / Code</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Message / Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/15">
                    {uploadResult.results.map((r, idx) => (
                      <tr key={idx} className="hover:bg-surface-container">
                        <td className="px-4 py-2.5 font-bold text-on-surface-variant">
                          #{r.row_index || idx + 2}
                        </td>
                        <td className="px-4 py-2.5 font-semibold text-on-surface">
                          {r.task_name || r.name || r.project_name || 'N/A'}
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className={`inline-flex rounded-md px-2 py-0.5 font-bold uppercase text-[10px] ${
                              r.status === 'SUCCESS'
                                ? 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/30'
                                : 'bg-error/10 text-error border border-error/30'
                            }`}
                          >
                            {r.status || 'UNKNOWN'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-on-surface-variant">
                          {r.error ? (
                            <span className="text-error font-medium">{r.error}</span>
                          ) : (
                            <span className="text-emerald-700 font-medium">Created successfully</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Results Action Buttons */}
          <div className="flex items-center justify-between pt-4">
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-2 rounded-xl border border-outline px-4 py-2.5 text-sm font-semibold text-on-surface hover:bg-surface-container-high transition cursor-pointer"
            >
              <span className="material-symbols-outlined text-base">restart_alt</span>
              Upload Another File
            </button>

            <button
              type="button"
              onClick={() => navigate(resolvedReturnUrl)}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-on-primary shadow-md shadow-primary/20 hover:opacity-90 transition cursor-pointer"
            >
              Go to {entityLabelPlural.charAt(0).toUpperCase() + entityLabelPlural.slice(1)} List
              <span className="material-symbols-outlined text-base">arrow_forward</span>
            </button>
          </div>
        </section>
      )}
    </div>
  )
}
